import {safeLocalStorage, safeSessionStorage} from "@/lib/utils/storage";

/** localStorage key holding the accumulated, normalized SMS campaign tags (JSON string[]). */
export const SMS_TAGS_STORAGE_KEY = "smsTags";
/** sessionStorage key prefix marking a tag whose click was already reported this browser session. */
export const SMS_TAG_CLICKED_SESSION_PREFIX = "smsTagClicked:";

/** Raw `?tag=` value accepted from the URL — case-sensitive (traffic-hash-style tags). */
const RAW_TAG_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;

/** Backend caps the attach payload at 20 tags. */
export const MAX_SMS_TAGS = 20;

/** localStorage key holding the SMS tags this browser has landed with, for Mixpanel (AC-994). */
export const SMS_TAG_TOUCHES_STORAGE_KEY = "smsTagTouches";

/** Bumped only when the meaning of a stored field changes; another version reads as absent. */
const SMS_TAG_TOUCHES_VERSION = 1;

/**
 * The SMS tags this browser has landed with (AC-994), replayed to Mixpanel as super-properties.
 *
 * Kept apart from `smsTags` on purpose: that set is the attach queue and is cleared as soon as
 * the backend has the tags, while these must stay on every later event. Never cleared — like the
 * first-touch attribution record, it describes the device's history, not one session.
 */
export interface SmsTagTouches {
    v: number;
    /** The first tag ever landed with — first touch, never overwritten. */
    first: string;
    /** The tag of the latest landing — last touch. */
    last: string;
    /** Every tag landed with, oldest first, at most `MAX_SMS_TAGS`. */
    all: string[];
}

/** Trim only — tag names are case-sensitive; returns null when validation fails. */
export function normalizeSmsTag(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!RAW_TAG_PATTERN.test(trimmed)) return null;
    return trimmed;
}

/** Reads the accumulated tags from localStorage (defensive against malformed JSON). */
export function readSmsTags(): string[] {
    const raw = safeLocalStorage.getItem(SMS_TAGS_STORAGE_KEY);
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.filter((t): t is string => typeof t === "string")
            : [];
    } catch {
        return [];
    }
}

/** Adds a normalized tag to the localStorage set (dedup + accumulation across sessions). */
export function persistSmsTag(tag: string): void {
    const tags = readSmsTags();
    if (tags.includes(tag)) return;
    tags.push(tag);
    safeLocalStorage.setItem(SMS_TAGS_STORAGE_KEY, JSON.stringify(tags));
}

/** The stored touches, or `null` if this browser never landed with a valid `?tag=`. */
export function readSmsTagTouches(): SmsTagTouches | null {
    const raw = safeLocalStorage.getItem(SMS_TAG_TOUCHES_STORAGE_KEY);
    if (!raw) return null;
    try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null) return null;
        const touches = parsed as Record<string, unknown>;
        if (touches.v !== SMS_TAG_TOUCHES_VERSION) return null;
        if (typeof touches.first !== "string" || typeof touches.last !== "string") return null;
        if (!Array.isArray(touches.all) || !touches.all.every((t) => typeof t === "string")) {
            return null;
        }
        return touches as unknown as SmsTagTouches;
    } catch {
        return null;
    }
}

/** Records a landing with a normalized tag: first stays, last moves, the list accumulates. */
export function rememberSmsTagTouch(tag: string): void {
    const touches = readSmsTagTouches();
    const all = touches?.all.includes(tag)
        ? touches.all
        : [...(touches?.all ?? []), tag].slice(-MAX_SMS_TAGS);
    const next: SmsTagTouches = {
        v: SMS_TAG_TOUCHES_VERSION,
        first: touches?.first ?? tag,
        last: tag,
        all,
    };
    safeLocalStorage.setItem(SMS_TAG_TOUCHES_STORAGE_KEY, JSON.stringify(next));
}

/** Clears the accumulated tags — called after a successful attach. */
export function clearSmsTags(): void {
    safeLocalStorage.removeItem(SMS_TAGS_STORAGE_KEY);
}

/**
 * Fire-and-forget public click report — one per tag per browser session.
 * No auth, no credentials; every error is swallowed so it can never block render.
 */
export function reportSmsTagClick(tag: string): void {
    const sessionKey = `${SMS_TAG_CLICKED_SESSION_PREFIX}${tag}`;
    if (safeSessionStorage.getItem(sessionKey)) return;
    // Mark before the request resolves: dedup is per browser session, not per success.
    safeSessionStorage.setItem(sessionKey, "1");

    const baseUrl = process.env.NEXT_PUBLIC_WALLET_API_URL;
    if (!baseUrl) return;
    try {
        void fetch(`${baseUrl}/api/v1/tags/${encodeURIComponent(tag)}/click`, {
            method: "POST",
            credentials: "omit",
            keepalive: true,
        }).catch(() => {});
    } catch {
        // never surface — click tracking is best-effort
    }
}

/**
 * Captures a raw `?tag=` value: validate → normalize → persist (attach queue + Mixpanel touches)
 * → report the click. Returns the normalized tag (or null when invalid). Safe to call from an effect.
 */
export function captureSmsTag(raw: string | null | undefined): string | null {
    const tag = normalizeSmsTag(raw);
    if (!tag) return null;
    persistSmsTag(tag);
    rememberSmsTagTouch(tag);
    reportSmsTagClick(tag);
    return tag;
}
